import React, { Fragment, useEffect, useState } from 'react';
import { AsyncSelectField, NumberField, SelectField } from "../../../../Utils/renderField/fieldsHookForms";
import { useFieldArray, useFormContext } from "react-hook-form";
import { maxFloatNumber } from '../../../../Utils/renderField/hookForms';
import { RenderKilogramo } from '../../../../Utils/renderField/renderReadField';
import { unidades_medida } from "constants";
import { ContenedorApi } from '../Api/ContenedorApi';
import "react-datepicker/dist/react-datepicker.css";

/**
 * Componente que representa un contenedor en el formulario de creación de recepciones.
 * @param {*} props propiedades del componente
 * @param {number} props.index índice del contenedor en el formulario
 * @param {boolean} props.disableRemove indica si se puede eliminar el contenedor
 * @param {function} props.quitarContenedor función para eliminar el contenedor
 * @param {Array} props.fincasDefault lista de fincas por defecto
 * @param {Array} props.tiposMateriaPrima lista de tipos de materia prima
 * @param {Array} props.productosLatex lista de productos de latex
 * @param {Array} props.productosSolidos lista de productos sólidos
 * @param {boolean} props.pesoManual indica si el peso es manual
 * @returns {JSX.Element} componente del contenedor
 */
const Contenedor = (props) => {
    const {
        index,
        disableRemove,
        quitarContenedor,
        fincasDefault,
        tiposMateriaPrima,
        productosLatex,
        productosSolidos,
        pesoManual,
    } = props;

    const {
        getFincas,

        pesoBascula,
        getPesoBascula,
    } = ContenedorApi();

    // __________ FORM __________
    const { control, register, formState: { errors }, watch, setValue } = useFormContext();

    const baseContainerForm = `contenedores.${index}`;

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${baseContainerForm}.productos`,
    });

    // __________ API __________
    // Obtener el peso de báscula para llenar automáticamente el peso de ingreso
    useEffect(() => {
        if (pesoManual || index !== 0) return;

        const interval = setInterval(() => {
            getPesoBascula();
        }, 1500);

        return () => clearInterval(interval);
    }, [pesoManual, getPesoBascula]);

    const pesoActual = watch(`contenedores.0.peso_bruto_ingreso`);

    useEffect(() => {
        if (index !== 0 || pesoManual || pesoBascula === pesoActual) return;

        setValue(`contenedores.0.peso_bruto_ingreso`, pesoBascula);
    }, [pesoBascula, pesoManual, setValue]);

    // __________ FUNCIONALIDAD __________
    const [opcionesProductos, setOpcionesProductos] = useState([]);
    const [latexMode, setLatexMode] = useState(false);

    const actualizarListadoProductos = () => {
        const tipoMateriaPrimaSeleccionado = watch(`${baseContainerForm}.tipo_materia_prima`);

        if (!tipoMateriaPrimaSeleccionado) {
            setOpcionesProductos([]);
        } else if (tipoMateriaPrimaSeleccionado.tipo === 10) {
            setOpcionesProductos(productosLatex);
            setLatexMode(true);
        } else {
            setOpcionesProductos(productosSolidos);
            setLatexMode(false);
        }

        // Limpiar productos y dejar solo el primero
        fields.map((_, index) => {
            remove(index);
        });
        append({});
    }

    return (
        <div className="crear-recepciones__contenedor mt-3">
            <button type="button" className="btn crear-recepciones__contenedor-remove-btn"
                onClick={() => quitarContenedor(index)} disabled={disableRemove}>
                ❌
            </button>
            <h5 className='mt-3 mb-4 text-center'>Contenedor #{index + 1}</h5>
            <div className="formulario-fields-row mt-0">
                <div className="formulario-field">
                    <label htmlFor="finca">FINCA</label>
                    <AsyncSelectField
                        control={control}
                        name={`${baseContainerForm}.finca`}
                        errors={errors}
                        rules={{
                            required: "Este campo es obligatorio",
                        }}
                        loadOptions={getFincas}
                        defaultOptions={fincasDefault}
                    />
                </div>
                <div className="formulario-field">
                    <label htmlFor="tipo_materia_prima">TIPO DE MATERIA PRIMA</label>
                    <SelectField
                        control={control}
                        name={`${baseContainerForm}.tipo_materia_prima`}
                        errors={errors}
                        rules={{
                            required: "Este campo es obligatorio",
                        }}
                        options={tiposMateriaPrima}
                        onChange={() => {
                            actualizarListadoProductos();
                        }}
                    />
                </div>
                {index <= 0 ?
                    <div className="formulario-field">
                        <label htmlFor="peso_bruto_ingreso">PESO BRUTO DE INGRESO</label>
                        <NumberField
                            control={control}
                            name={`${baseContainerForm}.peso_bruto_ingreso`}
                            errors={errors}
                            allowNegative={false}
                            onEnterAction={() => { }}
                            suffix=" Kg"
                            rules={{
                                required: "Este campo es obligatorio",
                                min: { value: 1, message: "Debe ser al menos 1" },
                                max: { value: maxFloatNumber, message: "El número es muy grande" },
                            }}
                            disabled={!pesoManual}
                            title={pesoManual ? "" : "Peso tomado de báscula"}
                        />
                    </div>
                    :
                    <div className="formulario-field"></div>
                }
            </div>
            {/* mostrar luego de que seleccionen tipo de materia prima */}
            {opcionesProductos.length > 0 &&
                <Fragment>
                    <div className="new-table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Acción</th>
                                    <th>Materia Prima</th>
                                    <th>Peso Finca</th>
                                    <th>Unidad</th>
                                    <th>Kilogramos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((item, index) => {
                                    const baseProductForm = `${baseContainerForm}.productos[${index}]`;

                                    const pesoFinca = watch(`${baseProductForm}.peso_finca`);
                                    const unidad = watch(`${baseProductForm}.unidad`);

                                    let pesoKg = 0;
                                    if (unidad && pesoFinca) {
                                        pesoKg = pesoFinca * unidad.value;
                                    }

                                    // setValue(`${baseProductForm}.peso_finca_kg`, pesoKg);

                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                {index > 0 && (
                                                    <div className='d-flex justify-content-center'>
                                                        <img src={require('assets/img/icons/borrar.png')} alt="Borrar"
                                                            title="Borrar" className="action_img"
                                                            onClick={() => remove(index)}
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <SelectField
                                                    control={control}
                                                    name={`${baseProductForm}.materia_prima`}
                                                    errors={errors}
                                                    rules={{
                                                        required: "Este campo es obligatorio",
                                                    }}
                                                    options={opcionesProductos}
                                                />
                                            </td>
                                            <td>
                                                <NumberField
                                                    control={control}
                                                    name={`${baseProductForm}.peso_finca`}
                                                    errors={errors}
                                                    allowNegative={false}
                                                    onEnterAction={() => { }}
                                                    rules={{
                                                        required: "Este campo es obligatorio",
                                                        min: { value: 0, message: "Debe ser al menos 0" },
                                                        max: { value: maxFloatNumber, message: "El número es muy grande" },
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <SelectField
                                                    control={control}
                                                    name={`${baseProductForm}.unidad`}
                                                    errors={errors}
                                                    rules={{
                                                        required: "Este campo es obligatorio",
                                                    }}
                                                    options={unidades_medida}
                                                />
                                            </td>
                                            <td className="text-right th--no-wrap">
                                                <RenderKilogramo value={pesoKg} />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    {!latexMode &&
                        <button type="button"
                            className="btn btn-outline-info mt-3 crear-recepciones__contenedor-add-materia-btn"
                            onClick={() => append({})}>
                            AGREGAR MATERIA PRIMA
                        </button>
                    }
                </Fragment>
            }
        </div>
    )
}

export default Contenedor
